import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AppContext = createContext(null);

const THEME_KEY = 'amp_theme';
const TEAM_KEY = 'amp_selected_team';
const SETTINGS_KEY = 'amp_settings';

export const DEFAULT_SETTINGS = {
  showGreeting: true,
  showAnimations: false,
};

const loadSettings = () => {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        showGreeting: parsed.showGreeting ?? DEFAULT_SETTINGS.showGreeting,
        showAnimations: parsed.showAnimations ?? DEFAULT_SETTINGS.showAnimations,
      };
    }
  } catch (error) {
    console.error('Ошибка загрузки настроек:', error);
  }
  return { ...DEFAULT_SETTINGS };
};

export const AppProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => localStorage.getItem(THEME_KEY) || 'light');
  const [selectedTeam, setSelectedTeamState] = useState(() => localStorage.getItem(TEAM_KEY) || '');
  const [csvData, setCsvData] = useState([]);
  const [settings, setSettingsState] = useState(loadSettings);

  useEffect(() => {
    document.body.className = theme === 'dark' ? 'dark-theme' : '';
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  useEffect(() => {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  }, [settings]);

  const setTheme = useCallback((nextTheme) => {
    setThemeState(nextTheme);
  }, []);

  const setSelectedTeam = useCallback((teamName) => {
    setSelectedTeamState(teamName);
    if (teamName) {
      localStorage.setItem(TEAM_KEY, teamName);
    } else {
      localStorage.removeItem(TEAM_KEY);
    }
  }, []);

  const updateSetting = useCallback((key, value) => {
    setSettingsState((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const resetSettings = useCallback(() => {
    setSettingsState({ ...DEFAULT_SETTINGS });
  }, []);

  const value = {
    theme,
    setTheme,
    selectedTeam,
    setSelectedTeam,
    csvData,
    setCsvData,
    settings,
    updateSetting,
    resetSettings,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};
